
import { useState } from "react";
import { Mic, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import AnalysisUploader from "@/components/AnalysisUploader";
import AnalysisResults, { ResultLevel } from "@/components/AnalysisResults";
import { analyzeVoice } from "@/services/fraudDetection/mediaAnalysis";
import { saveDetectionResult } from "@/services/fraudDetection/db/saveResult";
import { getUserDetectionHistory } from "@/services/fraudDetection/db/getUserHistory";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { determineTextAnalysisLevel } from "@/utils/analysisUtils";

const VoiceAnalysis = () => {
  const [results, setResults] = useState<{
    results: {
      type: string;
      level: ResultLevel;
      score: number;
      details: string;
    }[];
    overallStatus: ResultLevel;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { profile } = useAuth();

  const handleAnalyze = async (file: File | string) => {
    if (typeof file !== 'object') {
      toast({
        title: "Invalid input",
        description: "Please upload an audio file",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Call the actual voice analysis service
      const analysisResult = await analyzeVoice(file);
      
      // Format results for display
      const detectionResults = [
        {
          type: "Voice Synthesis",
          level: analysisResult.score > 0.7 ? "fraudulent" as ResultLevel : 
                 analysisResult.score > 0.4 ? "suspicious" as ResultLevel : 
                 "safe" as ResultLevel,
          score: Math.round(analysisResult.score * 100),
          details: "Analysis for AI-generated voice patterns using acoustic fingerprinting."
        },
        {
          type: "Natural Prosody",
          level: analysisResult.flags.some(f => f.includes("prosody")) ? 
                 "fraudulent" as ResultLevel : "safe" as ResultLevel,
          score: analysisResult.flags.some(f => f.includes("prosody")) ? 
                 85 : 30,
          details: "Analysis of rhythm, stress, and intonation patterns."
        },
        {
          type: "Audio Artifacts",
          level: determineTextAnalysisLevel(analysisResult.score * 70),
          score: Math.round(analysisResult.score * 70),
          details: "Detection of technical anomalies in audio signal consistent with synthesis."
        },
        {
          type: "Acoustic Features",
          level: analysisResult.confidence < 0.7 ? 
                 "suspicious" as ResultLevel : "safe" as ResultLevel,
          score: Math.round((1 - analysisResult.confidence) * 100),
          details: "Spectral and frequency distribution analysis."
        },
      ];
      
      // Calculate overall status based on highest risk detection
      const riskLevels = {
        "fraudulent": 2,
        "suspicious": 1,
        "safe": 0
      };
      
      const maxRiskLevel = detectionResults.reduce((max, result) => {
        return Math.max(max, riskLevels[result.level]);
      }, 0);
      
      const overallStatus = Object.keys(riskLevels).find(
        key => riskLevels[key as keyof typeof riskLevels] === maxRiskLevel
      ) as ResultLevel;
      
      const formattedResults = {
        results: detectionResults,
        overallStatus
      };
      
      // Save results to database if user is logged in
      if (profile?.id) {
        try {
          await saveDetectionResult(
            profile.id, 
            "voice_analysis", 
            analysisResult
          );
          console.log("Voice analysis result saved to database");
        } catch (error) {
          console.error("Failed to save voice analysis result:", error);
        }
      }
      
      setResults(formattedResults);
      
      toast({
        title: "Analysis Complete",
        description: `Analysis completed with ${overallStatus} result`,
        variant: overallStatus === "fraudulent" ? "destructive" : "default"
      });
    } catch (error) {
      console.error("Analysis error:", error);
      toast({
        title: "Analysis Failed",
        description: "There was an error processing your voice sample. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewHistory = async () => {
    if (!profile?.id) {
      toast({
        title: "Authentication Required",
        description: "Please log in to view your analysis history",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsLoading(true);
      // Updated to use proper params object
      const history = await getUserDetectionHistory({
        userId: profile.id,
        analysisType: "voice_analysis"
      });
      console.log("User voice analysis history:", history);
      
      if (!history.data || history.data.length === 0) {
        toast({
          title: "No History Found",
          description: "You haven't performed any voice analyses yet",
        });
      } else {
        toast({
          title: "History Retrieved",
          description: `Found ${history.data.length} previous voice analyses`,
        });
        // Ideally we would display this history in the UI
      }
    } catch (error) {
      console.error("Failed to fetch history:", error);
      toast({
        title: "Error",
        description: "Failed to retrieve analysis history",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewAnalysis = () => {
    setResults(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Voice Analysis</h1>
        <p className="text-muted-foreground">
          Detect AI-generated voices, voice cloning, and audio manipulation
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <AnalysisUploader
            title="Voice Analysis"
            description="Upload an audio file to analyze for synthetic voices"
            acceptedFileTypes=".mp3,.wav,.m4a,.ogg"
            icon={<Mic className="h-5 w-5 text-fraudeye-accent" />}
            onAnalyze={handleAnalyze}
            allowText={false}
          />

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>How It Works</CardTitle>
              <CardDescription>
                Our voice analysis uses multiple detection techniques
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-medium">Voice Synthesis Detection</h3>
                <p className="text-sm text-muted-foreground">
                  Identifies patterns common in AI-generated voices using advanced acoustic models.
                </p>
              </div>
              <div>
                <h3 className="font-medium">Natural Prosody Analysis</h3>
                <p className="text-sm text-muted-foreground">
                  Examines speech rhythm, stress patterns, and intonation for unnatural variations.
                </p>
              </div>
              <div>
                <h3 className="font-medium">Audio Artifact Detection</h3>
                <p className="text-sm text-muted-foreground">
                  Identifies digital artifacts, unusual pauses, and other technical anomalies.
                </p>
              </div>
              <div>
                <h3 className="font-medium">Acoustic Feature Analysis</h3>
                <p className="text-sm text-muted-foreground">
                  Analyzes frequency distribution, formants, and other acoustic properties.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          {results ? (
            <AnalysisResults {...results} />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Mic className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="font-medium mb-2">No Analysis Results Yet</h3>
                  <p className="text-sm text-muted-foreground mb-6 max-w-md">
                    Upload an audio file to begin analysis. Results will appear here once processing is complete.
                  </p>
                  <Alert variant="default" className="max-w-md border-fraudeye-accent/50 bg-fraudeye-accent/10">
                    <AlertTriangle className="h-4 w-4 text-fraudeye-accent" />
                    <AlertTitle>Tip</AlertTitle>
                    <AlertDescription>
                      For best results, use high-quality recordings with minimal background noise.
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <Button 
              className="w-full" 
              variant="outline" 
              onClick={handleViewHistory}
              disabled={isLoading}
            >
              View History
            </Button>
            <Button 
              className="w-full bg-fraudeye-accent hover:bg-fraudeye-highlight"
              onClick={handleNewAnalysis}
              disabled={isLoading}
            >
              New Analysis
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceAnalysis;
